import { useState, useRef, useCallback, useEffect } from 'react'

interface SpeechRecognitionEvent {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionErrorEvent {
  error: string
  message?: string
}

export interface UseSpeechRecognitionReturn {
  isSupported: boolean
  isListening: boolean
  transcript: string
  interimResult: string
  error: string | null
  startListening: () => void
  stopListening: () => void
  resetTranscript: () => void
}

export function useSpeechRecognition(): UseSpeechRecognitionReturn {
  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
    : null

  const isSupported = !!SpeechRecognition

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimResult, setInterimResult] = useState('')
  const [error, setError] = useState<string | null>(null)

  const recognitionRef = useRef<any>(null)
  const isListeningRef = useRef(false)
  const transcriptRef = useRef('')

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setError('Je browser ondersteunt geen spraakherkenning.')
      return
    }

    setError(null)
    setInterimResult('')

    const recognition = new SpeechRecognition()
    recognition.lang = 'nl-NL'
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = ''
      let finalTranscript = transcriptRef.current

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          const text = result[0].transcript.trim()
          if (text) {
            finalTranscript += (finalTranscript ? ' ' : '') + text
          }
        } else {
          interim += result[0].transcript
        }
      }

      transcriptRef.current = finalTranscript
      setTranscript(finalTranscript)
      setInterimResult(interim)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // These errors are recoverable — recognition will auto-restart via onend
      if (event.error === 'no-speech' || event.error === 'network' || event.error === 'aborted') {
        return
      }

      if (event.error === 'not-allowed') {
        setError('Microfoontoegang geweigerd. Sta microfoon toe voor spraakherkenning.')
        setIsListening(false)
        isListeningRef.current = false
        return
      }

      setError(`Spraakherkenning fout: ${event.error}`)
    }

    recognition.onend = () => {
      // Browser stops recognition after silence — restart if still listening
      if (isListeningRef.current) {
        try {
          recognition.start()
        } catch {
          // Ignore start errors during restart
        }
      }
    }

    try {
      recognition.start()
      recognitionRef.current = recognition
      setIsListening(true)
      isListeningRef.current = true
    } catch {
      setError('Kon spraakherkenning niet starten.')
    }
  }, [SpeechRecognition])

  const stopListening = useCallback(() => {
    isListeningRef.current = false
    setIsListening(false)
    setInterimResult('')

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch {
        // Ignore stop errors
      }
      recognitionRef.current = null
    }
  }, [])

  const resetTranscript = useCallback(() => {
    transcriptRef.current = ''
    setTranscript('')
    setInterimResult('')
    setError(null)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isListeningRef.current = false
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch {
          // Ignore
        }
        recognitionRef.current = null
      }
    }
  }, [])

  return {
    isSupported,
    isListening,
    transcript,
    interimResult,
    error: error,
    startListening,
    stopListening,
    resetTranscript,
  }
}
